import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { formatCPF, formatCEP, formatPhone, onlyDigits, formatMoney, RENDA_FAIXA1_LIMITE } from "@/lib/api";

const ESTADOS_CIVIS = ["Solteiro", "Casado", "União Estável", "Divorciado", "Viúvo"];
const ESCOLARIDADES = ["Não Alfabetizado", "Fundamental Incompleto", "Fundamental Completo", "Médio Incompleto", "Médio Completo", "Superior Incompleto", "Superior Completo"];

// Helper para campos de radio Sim/Não.
function SimNao({ label, value, onChange, idBase }) {
  return (
    <div>
      <Label>{label}</Label>
      <RadioGroup value={value ? "sim" : "nao"} onValueChange={(v) => onChange(v === "sim")} className="flex gap-4 mt-1">
        <div className="flex items-center gap-2"><RadioGroupItem value="sim" id={`${idBase}-s`} /><Label htmlFor={`${idBase}-s`}>Sim</Label></div>
        <div className="flex items-center gap-2"><RadioGroupItem value="nao" id={`${idBase}-n`} /><Label htmlFor={`${idBase}-n`}>Não</Label></div>
      </RadioGroup>
    </div>
  );
}

export function StepResponsavel({ form, update }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2"><Label>Nome Completo *</Label><Input value={form.nome_completo} onChange={(e) => update("nome_completo", e.target.value)} data-testid="form-nome" /></div>
      <div><Label>CPF *</Label><Input value={form.cpf} onChange={(e) => update("cpf", formatCPF(e.target.value))} data-testid="form-cpf" /></div>
      <div><Label>RG</Label><Input value={form.rg} onChange={(e) => update("rg", e.target.value)} data-testid="form-rg" /></div>
      <div><Label>CNH (opcional)</Label><Input value={form.cnh} onChange={(e) => update("cnh", e.target.value)} data-testid="form-cnh" /></div>
      <div><Label>Data de Nascimento *</Label><Input type="date" value={form.data_nascimento} onChange={(e) => update("data_nascimento", e.target.value)} data-testid="form-nasc" /></div>
      <div>
        <Label>Sexo *</Label>
        <Select value={form.sexo} onValueChange={(v) => update("sexo", v)}>
          <SelectTrigger data-testid="form-sexo"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Masculino">Masculino</SelectItem>
            <SelectItem value="Feminino">Feminino</SelectItem>
            <SelectItem value="Outro">Outro</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Estado Civil *</Label>
        <Select value={form.estado_civil} onValueChange={(v) => update("estado_civil", v)}>
          <SelectTrigger data-testid="form-estado-civil"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>{ESTADOS_CIVIS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Telefone *</Label><Input value={form.telefone} onChange={(e) => update("telefone", formatPhone(e.target.value))} data-testid="form-telefone" /></div>
      <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => update("whatsapp", formatPhone(e.target.value))} data-testid="form-whatsapp" /></div>
      <div className="md:col-span-2"><Label>E-mail *</Label><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="form-email" /><p className="text-xs text-slate-500 mt-1">Obrigatório — receberá o comprovante e futuras notificações.</p></div>
    </div>
  );
}

export function StepConjuge({ form, update, isCasado }) {
  if (!isCasado) {
    return <Alert><AlertDescription>Esta etapa não se aplica ao seu estado civil. Avance para a próxima.</AlertDescription></Alert>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2"><Label>Nome Completo do Cônjuge *</Label><Input value={form.conjuge_nome} onChange={(e) => update("conjuge_nome", e.target.value)} data-testid="form-conjuge-nome" /></div>
      <div><Label>CPF do Cônjuge *</Label><Input value={form.conjuge_cpf} onChange={(e) => update("conjuge_cpf", formatCPF(e.target.value))} data-testid="form-conjuge-cpf" /></div>
      <div><Label>RG do Cônjuge</Label><Input value={form.conjuge_rg} onChange={(e) => update("conjuge_rg", e.target.value)} /></div>
      <div><Label>Data de Nascimento</Label><Input type="date" value={form.conjuge_data_nascimento} onChange={(e) => update("conjuge_data_nascimento", e.target.value)} /></div>
    </div>
  );
}

export function StepFamilia({ form, update }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div><Label>Quantidade de moradores</Label><Input type="number" min="1" value={form.qtd_moradores} onChange={(e) => update("qtd_moradores", parseInt(e.target.value || 0))} data-testid="form-moradores" /></div>
      <div><Label>Quantidade de dependentes</Label><Input type="number" min="0" value={form.qtd_dependentes} onChange={(e) => update("qtd_dependentes", parseInt(e.target.value || 0))} data-testid="form-dependentes" /></div>
      <div><Label>Quantidade de crianças</Label><Input type="number" min="0" value={form.qtd_criancas} onChange={(e) => update("qtd_criancas", parseInt(e.target.value || 0))} /></div>
      <div><Label>Quantidade de idosos</Label><Input type="number" min="0" value={form.qtd_idosos} onChange={(e) => update("qtd_idosos", parseInt(e.target.value || 0))} /></div>
      <div><Label>Quantidade de PCD</Label><Input type="number" min="0" value={form.qtd_pcd} onChange={(e) => update("qtd_pcd", parseInt(e.target.value || 0))} /></div>
      <div>
        <Label>Escolaridade</Label>
        <Select value={form.grau_escolaridade} onValueChange={(v) => update("grau_escolaridade", v)}>
          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>{ESCOLARIDADES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label>Ocupação Profissional</Label><Input value={form.ocupacao} onChange={(e) => update("ocupacao", e.target.value)} /></div>
    </div>
  );
}

const TIPOS_MORADIA = ["Própria", "Alugada", "Cedida"];

export function StepEndereco({ form, update, onCepBlur }) {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>CEP *</Label><Input value={form.cep} onChange={(e) => { const v = formatCEP(e.target.value); update("cep", v); onCepBlur(v); }} placeholder="00000-000" data-testid="form-cep" /></div>
        <div><Label>Rua *</Label><Input value={form.rua} onChange={(e) => update("rua", e.target.value)} data-testid="form-rua" /></div>
        <div><Label>Número *</Label><Input value={form.numero} onChange={(e) => update("numero", e.target.value)} data-testid="form-numero" /></div>
        <div><Label>Complemento</Label><Input value={form.complemento} onChange={(e) => update("complemento", e.target.value)} /></div>
        <div><Label>Bairro *</Label><Input value={form.bairro} onChange={(e) => update("bairro", e.target.value)} data-testid="form-bairro" /></div>
        <div><Label>Cidade</Label><Input value={form.cidade} onChange={(e) => update("cidade", e.target.value)} /></div>
        <div><Label>Estado</Label><Input value={form.estado} onChange={(e) => update("estado", e.target.value)} maxLength={2} /></div>
      </div>
      <div className="rounded-md border border-slate-200 p-4 bg-slate-50/60 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Tipo de moradia</Label>
            <Select value={form.tipo_moradia || ""} onValueChange={(v) => update("tipo_moradia", v)}>
              <SelectTrigger data-testid="form-tipo-moradia"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{TIPOS_MORADIA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.tipo_moradia === "Própria" && (
            <SimNao label="A casa está em área de risco?" value={form.area_de_risco} onChange={(v) => update("area_de_risco", v)} idBase="area-risco" />
          )}
        </div>
      </div>
    </div>
  );
}

export function StepEleitoral({ form, update }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div><Label>Título de Eleitor</Label><Input value={form.titulo_eleitor} onChange={(e) => update("titulo_eleitor", e.target.value)} /></div>
      <div><Label>Zona Eleitoral</Label><Input value={form.zona_eleitoral} onChange={(e) => update("zona_eleitoral", e.target.value)} /></div>
      <div><Label>Seção Eleitoral</Label><Input value={form.secao_eleitoral} onChange={(e) => update("secao_eleitoral", e.target.value)} /></div>
    </div>
  );
}

export function StepSocial({ form, update }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <SimNao label="Possui Cadastro Único?" value={form.possui_cadunico} onChange={(v) => update("possui_cadunico", v)} idBase="cad" />
      <div><Label>NIS</Label><Input value={form.nis} onChange={(e) => update("nis", onlyDigits(e.target.value))} data-testid="form-nis" /></div>
      <SimNao label="Beneficiário de programa social?" value={form.beneficiario_programa} onChange={(v) => update("beneficiario_programa", v)} idBase="bp" />
      {form.beneficiario_programa && <div><Label>Qual programa?</Label><Input value={form.qual_programa} onChange={(e) => update("qual_programa", e.target.value)} /></div>}
      <SimNao label="Recebe Bolsa Família?" value={form.recebe_bolsa_familia} onChange={(v) => update("recebe_bolsa_familia", v)} idBase="bf" />
    </div>
  );
}

const TIPOS_DEFICIENCIA = ["Física", "Visual", "Auditiva", "Intelectual", "Múltipla", "Mental/Psicossocial", "Outra"];

export function StepPNE({ form, update }) {
  return (
    <div className="space-y-5" data-testid="step-pne">
      <div className="rounded-md border border-slate-200 p-4 bg-slate-50/60">
        <div className="grid md:grid-cols-2 gap-4">
          <SimNao label="Há Portador de Necessidades Especiais (PNE) na família?" value={form.possui_pcd} onChange={(v) => update("possui_pcd", v)} idBase="pne" />
          {form.possui_pcd && (
            <div>
              <Label>Quantas pessoas com deficiência?</Label>
              <Input type="number" min="0" value={form.qtd_pcd} onChange={(e) => update("qtd_pcd", parseInt(e.target.value || 0))} data-testid="form-qtd-pcd" />
            </div>
          )}
        </div>
      </div>

      {form.possui_pcd && (
        <div className="rounded-md border border-slate-200 p-4 space-y-4 bg-white">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Tipo de deficiência</Label>
              <Select value={form.tipo_deficiencia || ""} onValueChange={(v) => update("tipo_deficiencia", v)}>
                <SelectTrigger data-testid="form-tipo-deficiencia"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{TIPOS_DEFICIENCIA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>CID <span className="text-xs text-slate-500">(Classificação Internacional de Doenças)</span></Label>
              <Input value={form.cid_pne || ""} onChange={(e) => update("cid_pne", e.target.value)} placeholder="Ex: G80, H54, F70..." data-testid="form-cid-pne" />
            </div>
            <div className="md:col-span-2">
              <Label>Descrição da necessidade especial</Label>
              <Textarea value={form.descricao_pcd || ""} onChange={(e) => update("descricao_pcd", e.target.value)} rows={3} placeholder="Descreva a condição e adaptações necessárias na moradia" data-testid="form-descricao-pcd" />
            </div>
          </div>
          <SimNao label="A pessoa com PNE é o(a) responsável familiar?" value={form.pne_e_responsavel} onChange={(v) => update("pne_e_responsavel", v)} idBase="pne-resp" />
        </div>
      )}
    </div>
  );
}

export function StepIDOSO({ form, update }) {
  return (
    <div className="space-y-5" data-testid="step-idoso">
      <div className="rounded-md border border-slate-200 p-4 bg-slate-50/60">
        <div className="grid md:grid-cols-2 gap-4">
          <SimNao label="Há idoso (60+ anos) na residência?" value={form.possui_idoso} onChange={(v) => update("possui_idoso", v)} idBase="idoso" />
          {form.possui_idoso && (
            <div>
              <Label>Quantos idosos?</Label>
              <Input type="number" min="0" value={form.qtd_idosos} onChange={(e) => update("qtd_idosos", parseInt(e.target.value || 0))} data-testid="form-qtd-idosos" />
            </div>
          )}
        </div>
      </div>

      {form.possui_idoso && (
        <div className="rounded-md border border-slate-200 p-4 space-y-4 bg-white">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Idade do idoso (anos)</Label>
              <Input value={form.idade_idoso || ""} onChange={(e) => update("idade_idoso", e.target.value)} placeholder="Ex: 68" data-testid="form-idade-idoso" />
            </div>
            <div className="md:col-span-2">
              <Label>Observações sobre o idoso</Label>
              <Textarea value={form.descricao_idoso || ""} onChange={(e) => update("descricao_idoso", e.target.value)} rows={3} placeholder="Mobilidade reduzida, dependência de cuidador, etc." data-testid="form-descricao-idoso" />
            </div>
          </div>
          <SimNao label="O idoso é o(a) responsável familiar?" value={form.idoso_e_responsavel} onChange={(v) => update("idoso_e_responsavel", v)} idBase="idoso-resp" />
        </div>
      )}
    </div>
  );
}

export function StepRenda({ form, update, rendaTotal }) {
  const fora = rendaTotal > RENDA_FAIXA1_LIMITE;
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div><Label>Renda do Responsável (R$)</Label><Input type="number" min="0" step="0.01" value={form.renda_responsavel} onChange={(e) => update("renda_responsavel", parseFloat(e.target.value || 0))} data-testid="form-renda-resp" /></div>
        <div><Label>Renda do Cônjuge (R$)</Label><Input type="number" min="0" step="0.01" value={form.renda_conjuge} onChange={(e) => update("renda_conjuge", parseFloat(e.target.value || 0))} /></div>
        <div><Label>Outras Rendas (R$)</Label><Input type="number" min="0" step="0.01" value={form.outras_rendas} onChange={(e) => update("outras_rendas", parseFloat(e.target.value || 0))} /></div>
      </div>
      <Alert className={fora ? "border-red-500 bg-red-50" : "border-green-500 bg-green-50"}>
        {fora ? <AlertCircle className="w-4 h-4 text-red-600" /> : <CheckCircle2 className="w-4 h-4 text-green-700" />}
        <AlertDescription className="text-sm" data-testid="renda-total-alert">
          <strong>Renda Familiar Total: {formatMoney(rendaTotal)}</strong>
          <div className="mt-1">{fora ? `Família fora dos critérios da Faixa 1 (limite ${formatMoney(RENDA_FAIXA1_LIMITE)}).` : "Família dentro da Faixa 1 do programa."}</div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export function StepDeclaracao({ form, update, rendaTotal }) {
  const fora = rendaTotal > RENDA_FAIXA1_LIMITE;
  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription className="text-sm">
          Confira os dados informados antes de finalizar. Após o envio, você poderá anexar documentos (CPF, RG, comprovante de residência, CadÚnico etc).
        </AlertDescription>
      </Alert>
      <div className="bg-slate-50 rounded p-4 text-sm space-y-1">
        <div><strong>Nome:</strong> {form.nome_completo}</div>
        <div><strong>CPF:</strong> {form.cpf}</div>
        <div><strong>Telefone:</strong> {form.telefone}</div>
        <div><strong>Endereço:</strong> {form.rua}, {form.numero} - {form.bairro}, {form.cidade}/{form.estado}</div>
        <div><strong>Renda total:</strong> {formatMoney(rendaTotal)} {fora && <span className="text-red-600">(fora da Faixa 1)</span>}</div>
      </div>
      <div className="flex items-start gap-3 p-4 border rounded">
        <Checkbox id="declaracao" checked={form.declaracao_veracidade} onCheckedChange={(c) => update("declaracao_veracidade", !!c)} data-testid="form-declaracao" />
        <Label htmlFor="declaracao" className="text-sm font-normal leading-relaxed cursor-pointer">
          Declaro, sob as penas da lei, que todas as informações prestadas neste formulário são verdadeiras, e estou ciente de que a falsidade das informações implicará no indeferimento do pedido e nas medidas cabíveis pela legislação vigente.
        </Label>
      </div>
    </div>
  );
}
